import actionTypes from './actionTypes';
import { invalidatesRequest, requestTypes, resultsStatKey } from './requestState';

const emptyResultsStat = () => ({ average: [], best: [], group_best_average: 0 });

const SRPOptions = [
  { label: 'EP vs BU', value: 'UTGvsBTN' },
  { label: 'EP vs BB', value: 'UTGvsBB' },
  { label: 'CO vs BU', value: 'COvsBTN' },
  { label: 'BU vs SB', value: 'BTNvsSB' },
  { label: 'BU vs BB', value: 'BTNvsBB' },
  { label: 'SB vs BB', value: 'SBvsBB' },
  { label: 'Limp Check', value: 'limpCheck' },
  { label: 'Limp Raise Call', value: 'limpRaiseCall' },
  { label: 'EP vs BU vs BB', value: 'UTGvsBTNvsBB' },
  { label: 'BU vs SB vs BB', value: 'BTNvsSBvsBB' },
];

const ThreeBetPotOptions = [
  { label: 'BU vs EP', value: 'BTNvsUTG' },
  { label: 'SB vs EP', value: 'SBvsUTG' },
  { label: 'BU vs CO', value: 'BTNvsCO' },
  { label: 'SB vs BU', value: 'SBvsBTN' },
  { label: 'BB vs SB', value: 'BBvsSB' },
  { label: 'SB vs CO vs BU', value: 'SBvsCOvsBTN' },
  { label: 'BU vs MP vs CO', value: 'BTNvsMPvsCO' },
];

const FourBetPotOptions = [
  { label: 'EP vs BU', value: 'UTGvsBTN' },
  { label: 'EP vs SB', value: 'UTGvsSB' },
  { label: 'CO vs BU', value: 'COvsBTN' },
  { label: 'BU vs SB', value: 'BTNvsSB' },
  { label: 'SB vs BB', value: 'SBvsBB' },
];

const initialState = {
  tableSize: '6max',
  stackSize: '100bb',
  positionType: 'SRP',
  position: ['BTNvsBB'],
  positionOptions: SRPOptions,
  resultsPositionType: { label: 'SRP', value: 'SRP' },
  resultsPfrType: { label: 'PFR IP', value: 'pfr_ip' },
  resultsPlayerPosition: { label: 'IP', value: 'ip' },
  resultsPosition: { label: 'EP vs BB check', value: 'EPvsBB_check' },
  resultsPreviousAction: { label: 'Vs Check', value: 'EPvsBB_check', action: 'check' },
  boardSuit: 'two-tone',
  boardType: 'unpaired',
  selectedBoard: '',
  selectedCards: [],
  boardSituation: [],
  searchValue: '',
  similarResponse: null,
  selectedHighCard: '',
  selectedHandType: null,
  highLowOption: { label: 'High', value: 'High' },
  pageMode: { label: 'Study', value: 'Study' },
  combo: '',
  nextCombo: '',
  resultsStat: {
    average: [],
    best: [],
    group_best_average: 0,
  },
  gameStarted: false,
};

const clearedSelectionState = {
  selectedBoard: '',
  selectedCards: [],
  boardSituation: [],
  searchValue: '',
  similarResponse: null,
  combo: '',
  nextCombo: '',
};

const definePositionOptions = (pos) => {
  switch (pos.value) {
    case 'SRP': {
      return SRPOptions;
    }
    case '3bP': {
      return ThreeBetPotOptions;
    }
    case '4bP': {
      return FourBetPotOptions;
    }
    default: {
      return SRPOptions;
    }
  }
};

const definePosition = (pos) => {
  switch (pos.value) {
    case 'SRP': {
      return ['BTNvsBB'];
    }
    case '3bP': {
      return ['BTNvsUTG'];
    }
    case '4bP': {
      return ['BTNvsSB'];
    }
    default: {
      return ['BTNvsBB'];
    }
  }
};

const reduceSelection = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.SET_DEFAULT: {
      return initialState;
    }
    case actionTypes.SET_TABLE_SIZE: {
      return {
        ...state,
        ...clearedSelectionState,
        tableSize: action.payload.value,
      };
    }
    case actionTypes.SET_STACK_SIZE: {
      return {
        ...state,
        ...clearedSelectionState,
        stackSize: action.payload.value,
      };
    }
    case actionTypes.SET_POSITION_TYPE: {
      return {
        ...state,
        ...clearedSelectionState,
        positionType: action.payload.value,
        positionOptions: definePositionOptions(action.payload),
        position: definePosition(action.payload),
      };
    }
    case actionTypes.SET_POSITION: {
      return {
        ...state,
        ...clearedSelectionState,
        position: [action.payload.value],
      };
    }
    case actionTypes.SET_RESULTS_POSITION_TYPE: {
      return {
        ...state,
        ...clearedSelectionState,
        resultsPositionType: action.payload,
      };
    }
    case actionTypes.SET_RESULTS_PFR_TYPE: {
      return {
        ...state,
        ...clearedSelectionState,
        resultsPfrType: action.payload,
      };
    }
    case actionTypes.SET_RESULTS_PLAYER_POSITION: {
      return {
        ...state,
        ...clearedSelectionState,
        resultsPlayerPosition: action.payload,
      };
    }
    case actionTypes.SET_RESULTS_POSITION: {
      return {
        ...state,
        ...clearedSelectionState,
        resultsPosition: action.payload,
      };
    }
    case actionTypes.SET_RESULTS_PREVIOUS_ACTION: {
      return {
        ...state,
        ...clearedSelectionState,
        resultsPreviousAction: action.payload,
      };
    }
    case actionTypes.SET_BOARD_SUIT: {
      return {
        ...state,
        ...clearedSelectionState,
        boardSuit: action.payload.value,
        highLowOption: { value: 'High', label: 'High' },
      };
    }
    case actionTypes.SET_BOARD_TYPE: {
      return {
        ...state,
        ...clearedSelectionState,
        boardType: action.payload.value,
        highLowOption: { value: 'High', label: 'High' },
      };
    }
    case actionTypes.SET_PAGE_MODE: {
      return {
        ...state,
        ...clearedSelectionState,
        pageMode: action.payload,
        gameStarted: false,
      };
    }
    case actionTypes.SET_SELECTED_HIGH_CARD: {
      if (action.payload.value === 'X') {
        return {
          ...state,
          ...clearedSelectionState,
          selectedHighCard: '',
          highLowOption: { value: 'High', label: 'High' },
        };
      }
      return {
        ...state,
        ...clearedSelectionState,
        selectedHighCard: action.payload.value,
        highLowOption: { value: 'High', label: 'High' },
      };
    }
    case actionTypes.SET_SELECTED_HAND_TYPE: {
      return {
        ...state,
        selectedCards: [],
        searchValue: '',
        similarResponse: null,
        combo: '',
        nextCombo: '',
        ...(state.pageMode?.value === 'Results' ? {
          selectedBoard: '',
          boardSituation: [],
        } : {}),
        selectedHandType: action.payload,
      };
    }
    case actionTypes.SET_SELECTED_BOARD: {
      const preserveSelection = action.payload.preserveSelection === true;

      return {
        ...state,
        selectedBoard: action.payload.value,
        selectedCards: preserveSelection ? state.selectedCards : [],
        boardSituation: [],
        searchValue: preserveSelection ? state.searchValue : '',
        similarResponse: null,
      };
    }
    case actionTypes.SET_HIGH_LOW: {
      return {
        ...state,
        highLowOption: action.payload,
      };
    }
    case actionTypes.SET_SELECTED_CARDS: {
      return {
        ...state,
        selectedCards: action.payload,
      };
    }
    case `${actionTypes.SET_BOARD_SITUATION}_SUCCESS`: {
      return {
        ...state,
        boardSituation: action.payload,
      };
    }
    case `${actionTypes.SET_COMBO}_SUCCESS`: {
      return {
        ...state,
        combo: action.payload,
      };
    }
    case `${actionTypes.GET_COMBO}_SUCCESS`: {
      return {
        ...state,
        combo: action.payload,
      };
    }
    case `${actionTypes.GET_NEXT_COMBO}_SUCCESS`: {
      return {
        ...state,
        nextCombo: action.payload,
      };
    }
    case actionTypes.CLEAR_NEXT_COMBO: {
      return {
        ...state,
        nextCombo: '',
      };
    }
    case `${actionTypes.GET_POSTFLOP_RESULTS_STAT}_SUCCESS`: {
      return {
        ...state,
        resultsStat: {
          average: action.payload?.average || [],
          best: action.payload?.best || [],
          group_best_average: action.payload?.group_best_average || 0,
          completed_best_average: action.payload?.completed_best_average || 0,
          positions_coverage: action.payload?.positions_coverage || 0,
          completed_positions: action.payload?.completed_positions || 0,
          total_positions: action.payload?.total_positions || 0,
        },
      };
    }
    case actionTypes.SET_GAME_STARTED: {
      return {
        ...state,
        gameStarted: action.payload,
      };
    }
    case actionTypes.SET_SEARCH_VALUE: {
      return {
        ...state,
        searchValue: action.payload,
      };
    }
    case `${actionTypes.SET_SIMILAR_RESPONSE}_SUCCESS`: {
      if (action.payload.currentCards) {
        return {
          ...state,
          similarResponse: action.payload.data,
          selectedCards: action.payload.currentCards,
        };
      }
      return {
        ...state,
        similarResponse: action.payload.data,
      };
    }
    default: {
      return state;
    }
  }
};

export default (state = initialState, action) => {
  const identity = action.meta?.postflopRequest;
  if (identity && identity.revision !== (state.requests?.[identity.kind]?.revision || 0)) {
    return state;
  }
  const nextState = reduceSelection(state, action);
  const requests = { ...state.requests };
  const cleared = {};
  Object.keys(requestTypes).forEach((kind) => {
    const type = requestTypes[kind];
    const previous = requests[kind] || {
      revision: 0, status: 'idle', error: '', key: '',
    };
    const started = action.type === type;
    const invalidated = invalidatesRequest(kind, action);
    const failed = action.type === `${type}_ERROR`;
    if (started || invalidated) {
      requests[kind] = {
        revision: previous.revision + 1,
        status: started ? 'loading' : 'idle',
        key: started && kind === 'resultsStat' ? resultsStatKey(action.payload) : '',
        error: '',
      };
    } else if (failed || action.type === `${type}_SUCCESS`) {
      requests[kind] = { ...previous, status: failed ? 'error' : 'success', error: action.error || '' };
    } else {
      requests[kind] = previous;
    }
    if (started || invalidated || failed) {
      if (kind === 'similar') cleared.similarResponse = null;
      if (kind === 'situation') cleared.boardSituation = [];
      if (kind === 'resultsStat') cleared.resultsStat = emptyResultsStat();
      if (kind === 'nextCombo') cleared.nextCombo = '';
      if (kind === 'combo' && (started || failed)) cleared.combo = '';
    }
  });
  return { ...nextState, ...cleared, requests };
};
